/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  useToast,
} from '@chakra-ui/react';
import { createAppointment } from '../services/api';
import { useClinic } from '../contexts/ClinicContext';
import Step1Location from './Steps/Step1Location';
import Step3ProviderTime from './Steps/Step3ProviderTime';
import Step2Details from './Steps/Step2Details';
import Step4ContactDetails from './Steps/Step4ContactDetails';
import Step5Review from './Steps/Step5Review';

const CAPTCHA_API_KEY = process.env.REACT_APP_WAF_CAPTCHA_API_KEY;

function StepIndicator({ step, current, onClick, isAccessible }) {
  const active = step === current;
  const completed = step < current;

  return (
    <Flex
      align="center"
      gap="8px"
      opacity={active ? 1 : 0.6}
      cursor={isAccessible ? 'pointer' : 'not-allowed'}
      onClick={isAccessible ? onClick : undefined}
      transition="all 0.2s"
      minH={{ base: '44px', md: 'auto' }}
      py={{ base: '4px', md: 0 }}
      _hover={isAccessible ? {
        opacity: 1,
        transform: 'translateY(-2px)'
      } : {}}
    >
      <Flex
        w={{ base: '40px', md: '30px' }}
        h={{ base: '40px', md: '30px' }}
        borderRadius="full"
        align="center"
        justify="center"
        fontWeight="600"
        bg={completed ? 'green.400' : active ? 'white' : 'whiteAlpha.300'}
        color={completed ? 'white' : active ? '#04385D' : 'white'}
        transition="all 0.2s"
        _hover={isAccessible ? {
          transform: 'scale(1.1)',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
        } : {}}
      >
        {step}
      </Flex>
      <Text color="white" fontWeight="600" fontSize={{ base: 'xs', sm: 'sm', md: 'md' }} lineHeight="short">
        {step === 1 && 'Location'}
        {step === 2 && 'Pet Details'}
        {step === 3 && 'Vet & Time'}
        {step === 4 && (
          <>
            <Text as="span" display={{ base: 'inline', md: 'none' }}>Contact</Text>
            <Text as="span" display={{ base: 'none', md: 'inline' }}>Contact Details</Text>
          </>
        )}
        {step === 5 && 'Review'}
      </Text>
    </Flex>
  );
}

function Scheduler() {
  const toast = useToast();
  const { clinic } = useClinic();
  // Per-clinic preference: when false, hide vet selection and the vet on review.
  const showVet = clinic?.show_assigned_vet !== false;
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [touchedSteps, setTouchedSteps] = useState({});
  const [captchaVerified, setCaptchaVerified] = useState(false);

  // Form state
  const [form, setForm] = useState({
    // New design fields
    location: null, // { id, name, address }
    provider: null, // { id, name, specialty }
    date: '', // 'YYYY-MM-DD'
    time: '', // '2:30 PM'
    issue: '',

    // Old fields: StepClientInfo
    returningClient: '', // 'new' | 'returning'
    numberOfPets: '', // '1' | '2'
    pets: [], // Array of pet objects: [{ animalName, reason, species, breed }, ...]
    clinician: '',

    // Old fields: StepClientDetails
    email: '',
    mobile: '',
    firstName: '',
    lastName: '',
    address: '',
    eirCode: '',
    marketingOptIn: false,
  });

  // Validation
  const isStep1Valid = !!form.location;
  const isStep2Valid = !!form.returningClient && !!form.numberOfPets && form.pets?.length === parseInt(form.numberOfPets || 0) &&
    form.pets.every(pet => pet.animalName && pet.reason && pet.species && pet.breed); // Pet Details validation
  const isStep3Valid = !!form.provider && !!form.date && !!form.time; // Vet & Time validation
  const isStep4Valid = !!form.firstName && !!form.lastName && !!form.email && !!form.mobile && !!form.eirCode;

  // Function to check if a specific step is valid
  const isStepValid = (step) => {
    switch (step) {
      case 1: return isStep1Valid;
      case 2: return isStep2Valid;
      case 3: return isStep3Valid;
      case 4: return isStep4Valid;
      case 5: return true; // Step 5 (review) is always accessible if previous steps are valid
      default: return false;
    }
  };

  // Function to navigate to a specific step with validation
  const goToStep = (targetStep) => {
    if (targetStep === currentStep) return; // Already on this step

    // Can always go to previous steps
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      return;
    }

    // For going forward, validate all previous steps
    for (let step = 1; step < targetStep; step++) {
      if (!isStepValid(step)) {
        let errorMessage = 'Please complete previous steps first';
        switch (step) {
          case 1: errorMessage = 'Please select a location first'; break;
          case 2: errorMessage = 'Please complete pet details first'; break;
          case 3: errorMessage = 'Please select a vet and time first'; break;
          case 4: errorMessage = 'Please complete contact details first'; break;
        }
        toast({ status: 'warning', title: errorMessage });
        return;
      }
    }

    setCurrentStep(targetStep);
  };

  const goNext = () => {
    if (currentStep === 1 && !isStep1Valid) {
      return toast({ status: 'warning', title: 'Please select a location' });
    }
    if (currentStep === 2 && !isStep2Valid) {
      setTouchedSteps((prev) => ({ ...prev, 2: true }));
      if (!form.returningClient) {
        return toast({ status: 'warning', title: 'Please specify if you are a new or returning client' });
      }
      if (!form.numberOfPets) {
        return toast({ status: 'warning', title: 'Please specify the number of pets' });
      }
      if (!form.pets?.length || form.pets.length !== parseInt(form.numberOfPets)) {
        return toast({ status: 'warning', title: 'Please provide details for all pets' });
      }
      const invalidPets = form.pets.filter(pet => !pet.animalName || !pet.reason || !pet.species || !pet.breed);
      if (invalidPets.length > 0) {
        return toast({ status: 'warning', title: 'Please complete all pet details (name, reason, species, breed)' });
      }
      return toast({ status: 'warning', title: 'Please complete pet details' });
    }
    if (currentStep === 3 && !isStep3Valid) {
      setTouchedSteps((prev) => ({ ...prev, 3: true }));
      if (!form.provider) {
        return toast({ status: 'warning', title: 'Please select a provider' });
      }
      if (!form.date || !form.time) {
        return toast({ status: 'warning', title: 'Please select a date and time' });
      }
      return toast({ status: 'warning', title: 'Please select a vet and time' });
    }
    if (currentStep === 4 && !isStep4Valid) {
      setTouchedSteps((prev) => ({ ...prev, 4: true }));
      if (!form.firstName) {
        return toast({ status: 'warning', title: 'Please provide your first name' });
      }
      if (!form.lastName) {
        return toast({ status: 'warning', title: 'Please provide your last name' });
      }
      if (!form.email) {
        return toast({ status: 'warning', title: 'Please provide your email address' });
      }
      if (!form.mobile) {
        return toast({ status: 'warning', title: 'Please provide your mobile number' });
      }
      if (!form.eirCode) {
        return toast({ status: 'warning', title: 'Please provide your Eircode' });
      }
      return toast({ status: 'warning', title: 'Please complete all required contact details' });
    }
    setCurrentStep((s) => Math.min(5, s + 1));
  };

  const goPrev = () => {
    const newStep = Math.max(1, currentStep - 1);
    // Clear dependent state when going back to earlier steps
    if (newStep === 1 && currentStep === 2) {
      // Going back to Step 1 from Step 2 (Pet Details): keep location, clear pet details
      setForm((f) => ({
        ...f,
        returningClient: '',
        numberOfPets: '',
        pets: [],
      }));
      setTouchedSteps((prev) => ({ ...prev, 2: false }));
    } else if (newStep === 2 && currentStep === 3) {
      // Going back to Step 2 (Pet Details) from Step 3 (Vet & Time): keep location and pet details, clear vet/time
      setForm((f) => ({
        ...f,
        provider: null,
        date: '',
        time: '',
      }));
      setTouchedSteps((prev) => ({ ...prev, 3: false }));
    } else if (newStep === 3 && currentStep === 4) {
      // Going back to Step 3 (Vet & Time) from Step 4: keep pet details and vet/time
      // No clearing needed for this transition
    }
    setCurrentStep(newStep);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Validate required fields
      const clinicId = form.location?.clinicId;
      if (!clinicId) {
        throw new Error('Clinic ID not found in selected location');
      }

      // Prepare appointment data for API
      const appointmentData = {
        clinicId: clinicId,
        vetId: form.assignedVet?.id || form.provider?.id, // Use selected clinician or fall back to provider
        providerId: form.provider?.id,
        date: form.date,
        time: form.time,
        title: form.pets?.[0]?.reason || 'Appointment',
        notes: form.pets?.map(pet => `${pet.animalName}: ${pet.reason}`).join('; ') || form.issue,
        returningClient: form.returningClient, // 'new' or 'returning'
        numberOfPets: form.numberOfPets,
        pets: form.pets, // Array of pet objects
        // Client contact info (used for new clients)
        email: form.email,
        mobile: form.mobile,
        firstName: form.firstName,
        lastName: form.lastName,
        address: form.address,
        eirCode: form.eirCode,
        // Additional info
        preferences: {
          marketingOptIn: form.marketingOptIn,
        },
      };


      // Call the API
      const response = await createAppointment(appointmentData);

      toast({
        status: 'success',
        title: 'Appointment Request Submitted!',
        description: 'Your appointment request has been sent to the clinic. You will receive a notification once they review and confirm your appointment.',
        duration: 5000,
      });
      setCurrentStep(6);
    } catch (error) {
      const description =
        (typeof error?.message === 'string' && error.message.trim()) ||
        'An unexpected error occurred. Please try again.';
      toast({
        status: 'error',
        title: 'Booking failed',
        description,
        duration: 7000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setCaptchaVerified(false);
    setForm({
      location: null,
      provider: null,
      date: '',
      time: '',
      issue: '',
      returningClient: '',
      numberOfPets: '',
      pets: [],
      clinician: '',
      email: '',
      mobile: '',
      firstName: '',
      lastName: '',
      address: '',
      eirCode: '',
      marketingOptIn: false,
    });
    setCurrentStep(1);
    setTouchedSteps({});
  };

  useEffect(() => {
    if (window.location.protocol !== 'https:') {
      console.log('[CAPTCHA] skipped — not HTTPS (local dev). Button will be enabled without verification.');
      return;
    }

    if (currentStep !== 5) {
      if (captchaVerified) {
        console.log('[CAPTCHA] left step 5 — resetting verification state.');
      }
      setCaptchaVerified(false);
      return;
    }

    // Dump the state of WAF globals immediately so you can see in DevTools
    // whether jsapi.js has loaded before the puzzle even tries to render.
    console.log('[CAPTCHA] step 5 reached — script status:', {
      AwsWafCaptcha: typeof window.AwsWafCaptcha,
      AwsWafIntegration: typeof window.AwsWafIntegration,
      apiKeyConfigured: !!CAPTCHA_API_KEY,
    });

    let attempts = 0;
    const maxAttempts = 50; // poll for up to 5 seconds (50 × 100 ms)
    const interval = setInterval(() => {
      attempts += 1;
      const container = document.getElementById('captcha-container');
      if (container && window.AwsWafCaptcha) {
        clearInterval(interval);
        console.log(`[CAPTCHA] AwsWafCaptcha ready after ~${attempts * 100} ms — calling renderCaptcha.`);
        window.AwsWafCaptcha.renderCaptcha(container, {
          apiKey: CAPTCHA_API_KEY,
          onSuccess: () => {
            console.log('[CAPTCHA] puzzle solved ✓ — Confirm Booking is now enabled.');
            setCaptchaVerified(true);
          },
          onError: (err) => {
            console.error('[CAPTCHA] renderCaptcha error ✗', err);
            toast({
              status: 'error',
              title: 'Verification unavailable',
              description: 'Could not load the security check. Please refresh the page and try again.',
              duration: 7000,
            });
          },
        });
      }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        console.warn('[CAPTCHA] AwsWafCaptcha not available after 5 s — check that jsapi.js loaded in the Network tab.');
      }
    }, 100);

    return () => clearInterval(interval);
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box maxW="900px" mx="auto" bg="white" borderRadius="16px" overflow="hidden" boxShadow="lg" w="100%">
      {/* Header with Step Indicators */}
      <Box bg="#04385D" color="white" px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }} textAlign="center">
        <Heading size="lg" mb="2" fontSize={{ base: 'xl', md: '2xl' }}>Schedule Appointment</Heading>
        <Flex justify="center" gap={{ base: 3, sm: 4, md: 8 }} mt="4" wrap="wrap" rowGap={{ base: 2, md: 3 }}>
          {[1, 2, 3, 4, 5].map((s) => {
            // A step is accessible if:
            // 1. It's the current step, or
            // 2. It's a previous step, or
            // 3. It's the next step and all previous steps are valid
            const isAccessible = s <= currentStep ||
              (s === currentStep + 1 &&
               Array.from({ length: s - 1 }, (_, i) => i + 1).every(step => isStepValid(step)));

            return (
              <StepIndicator
                key={s}
                step={s}
                current={currentStep}
                isAccessible={isAccessible}
                onClick={() => goToStep(s)}
              />
            );
          })}
        </Flex>
      </Box>

      {/* Step Content */}
      <Box px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }} minH="400px" maxW="100%">
        {currentStep === 1 && <Step1Location form={form} setForm={setForm} />}
        {currentStep === 2 && <Step2Details form={form} setForm={setForm} showErrors={!!touchedSteps[2]} />}
        {currentStep === 3 && <Step3ProviderTime form={form} setForm={setForm} showErrors={!!touchedSteps[3]} showVet={showVet} />}
        {currentStep === 4 && <Step4ContactDetails form={form} setForm={setForm} showErrors={!!touchedSteps[4]} />}
        {currentStep === 5 && (
          <>
            <Step5Review form={form} showVet={showVet} />
            <Box id="captcha-container" mt={6} />
          </>
        )}
        {currentStep === 6 && (
          <Box textAlign="center" py="10">
            <Flex 
              w="80px" 
              h="80px" 
              bg="green.400" 
              color="white" 
              borderRadius="full" 
              fontSize="40px" 
              align="center" 
              justify="center" 
              mx="auto" 
              mb="4"
            >
              ✓
            </Flex>
            <Heading size="md" mb="2">Appointment Request Submitted!</Heading>
            <Text color="gray.600">Your appointment request has been sent to the clinic for review.</Text>
            <Text color="gray.600" fontSize="sm" mt="2" wordBreak="break-word" overflowWrap="anywhere" px={{ base: 2, md: 0 }}>
              You will be notified at {form.email} once the clinic reviews your request.
            </Text>
            <Button 
              mt="6" 
              bg="#04385D" 
              color="white" 
              _hover={{ bg: '#032C47' }} 
              onClick={reset}
            >
              Schedule Another
            </Button>
          </Box>
        )}
      </Box>

      {/* Footer with Navigation Buttons */}
      {currentStep < 6 && (
        <Flex
          p={{ base: '16px', md: '20px 30px' }}
          bg="gray.50"
          direction={{ base: 'column-reverse', sm: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', sm: 'center' }}
          gap={3}
        >
          <Box w={{ base: '100%', sm: 'auto' }}>
            {currentStep > 1 && currentStep < 6 ? (
              <Button variant="solid" onClick={goPrev} minH="44px" w={{ base: '100%', sm: 'auto' }}>
                Back
              </Button>
            ) : <Box />}
          </Box>

          <Box w={{ base: '100%', sm: 'auto' }}>
            {currentStep < 5 && (
              <Button bg="#04385D" color="white" _hover={{ bg: '#032C47' }} onClick={goNext} minH="44px" w={{ base: '100%', sm: 'auto' }}>
                Next
              </Button>
            )}
            {currentStep === 5 && (
              <Button
                bg="#04385D"
                color="white"
                _hover={{ bg: '#032C47' }}
                onClick={submit}
                isLoading={submitting}
                loadingText="Confirming..."
                isDisabled={window.location.protocol === 'https:' && !captchaVerified}
                minH="44px"
                w={{ base: '100%', sm: 'auto' }}
              >
                Confirm Booking
              </Button>
            )}
          </Box>
        </Flex>
      )}
    </Box>
  );
}

export default Scheduler;
